// The rules for carrying an unfinished tasting from one device to another (opt-in, encrypted at the
// edge). Pure, so the edge's accept-or-refuse and the PWA's "which copy do I show" cannot disagree.

/** A draft nobody touched for this long is gone: the nightly purge removes it and readers hide it. */
export const TASTING_DRAFT_RETENTION_DAYS = 30;
/** Unfinished tastings kept per person; a tenth is already more than anyone has open at once. */
export const MAX_TASTING_DRAFTS = 10;
/** The plaintext snapshot of one tasting, which the edge encrypts before it is stored. */
export const TASTING_DRAFT_MAX_BYTES = 96 * 1024;
/** How far ahead of the edge's clock a device's stamp may be before it is refused as wrong. */
export const DRAFT_CLOCK_SKEW_MS = 5 * 60_000;

export interface Stamped {
  readonly updatedAt: string;
}

export interface DraftVerdict {
  readonly winner: "local" | "remote";
  /** The other copy held changes of its own that are now gone: the device that lost is told, once. */
  readonly replaced: boolean;
}

/** An unreadable stamp loses every comparison instead of throwing, so a damaged copy cannot win. */
const instant = (stamp: string): number => {
  const ms = Date.parse(stamp);
  return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
};

/**
 * Newest `updatedAt` wins. A tie keeps the local copy, so the device that wrote a draft is never
 * told it was replaced by its own write coming back.
 */
export function settleDraft(local: Stamped | undefined, remote: Stamped | undefined): DraftVerdict {
  if (remote === undefined) return { winner: "local", replaced: false };
  if (local === undefined) return { winner: "remote", replaced: false };
  return instant(remote.updatedAt) > instant(local.updatedAt)
    ? { winner: "remote", replaced: true }
    : { winner: "local", replaced: false };
}

/** A stamp beyond what clock skew explains: refused, so one wrong clock cannot pin a draft forever. */
export function isFutureStamp(stamp: string, now: Date): boolean {
  return instant(stamp) > now.getTime() + DRAFT_CLOCK_SKEW_MS;
}
